<template>
  <!-- Süsteem -> Seaded -> Objekt uues kujunduses: the same fields and saving,
       in sections, with the type as two buttons, the address's own warning the
       moment it matches an object, and what is about to be saved beside it. -->
  <div v-if="plate" class="obj-plate obj-list obj-form">
    <div class="obj-head">
      <div class="obj-head__text">
        <span class="obj-kicker obj-mid">{{ formType === types.CREATE ? 'OBJEKTID › LISA UUS' : 'OBJEKTID › MUUDA' }}</span>
        <div class="obj-title"><h1 class="obj-wide">{{ formType === types.CREATE ? 'Lisa uus objekt' : (object ? object.address : 'Objekt') }}</h1></div>
      </div>
    </div>
    <div v-if="loading" class="obj-card obj-empty">Laen…</div>
    <div v-else class="obj-form__grid">
      <v-form ref="observer" class="obj-card" @submit.prevent>
        <div class="obj-form__sec">
          <div class="obj-form__sh"><h2>Mis objekt</h2></div>
          <div class="obj-form__g2" role="radiogroup" aria-label="Tüüp">
            <button v-for="kind in kinds" :key="kind.value" type="button" role="radio" class="obj-form__kind"
              :aria-checked="form.type === kind.value ? 'true' : 'false'" @click="form.type = kind.value">
              <b>{{ kind.label }}</b><span>{{ kind.hint }}</span>
            </button>
          </div>
        </div>

        <div class="obj-form__sec">
          <div class="obj-form__sh"><h2>Aadress</h2><span>Otsi Maa-ameti aadressiregistrist — sihtnumber ja linn täituvad ise</span></div>
          <div class="obj-form__fld">
            <span class="obj-form__lbl">Aadress <span class="obj-req">*</span></span>
            <Autocomplete
              :rules="[rules.required]"
              v-model="addressSearch"
              searchMethod="searchAdsAddresses"
              itemKey="objects"
              item-text="fullAddress"
              hide-no-data
              validate-on="blur lazy"
              variant="outlined"
              density="compact"
              hide-details="auto"
              placeholder="Tänav ja maja, nt Kuuse 14-3"
            >
              <template #item="{ props, item }">
                <v-list-item v-bind="props" :title="item.raw.streetAddress || item.raw.fullAddress" :subtitle="item.raw.fullAddress">
                  <template v-if="item.raw.knownObjectId" #append>
                    <span class="obj-auto obj-auto--late">objekt olemas</span>
                  </template>
                </v-list-item>
              </template>
            </Autocomplete>
          </div>
          <!-- Said now, not after Salvesta. Flats, a courtyard house and the
               like are separate objects at one address, so it is a warning. -->
          <div v-if="existing" class="obj-form__warn" role="status">
            <v-icon size="18">mdi-alert-outline</v-icon>
            <span class="flex-grow-1">
              Sellel aadressil on juba objekt: <b>{{ existing.address }}</b>
              <template v-if="existing.client"> · {{ existing.client.fullName }}</template>
              · {{ existing.type === objectTypes.CONTRACT ? 'lepinguline' : 'tavaline' }}, {{ existing.calendarCount }} töökäsku.
              Korter, hoovimaja jms võivad olla eraldi objektid.
            </span>
            <router-link :to="{ name: 'object/view/info', params: { objectId: existing.id } }" target="_blank" class="obj-form__warnlink">Ava olemasolev</router-link>
          </div>
          <div class="obj-form__g3">
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Postiindeks</span>
              <v-text-field v-model="form.postcode" variant="outlined" density="compact" hide-details="auto">
                <template v-if="fromAds.postcode && fromAds.postcode === form.postcode" #append-inner><span class="obj-auto">aadressist</span></template>
              </v-text-field>
            </div>
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Linn <span class="obj-req">*</span></span>
              <v-select :rules="[rules.required]" v-model="form.cityId" :items="cities" item-title="title" item-value="id" variant="outlined" density="compact" hide-details="auto">
                <template v-if="fromAds.cityId && fromAds.cityId === form.cityId" #append-inner><span class="obj-auto">aadressist</span></template>
              </v-select>
            </div>
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Riik <span class="obj-req">*</span></span>
              <v-select :rules="[rules.required]" v-model="form.countryId" :items="countries" item-title="title" item-value="id" variant="outlined" density="compact" hide-details="auto" />
            </div>
          </div>
        </div>

        <div class="obj-form__sec">
          <div class="obj-form__sh"><h2>Klient</h2></div>
          <div class="obj-form__g2">
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Klient</span>
              <autocomplete v-model="form.client" item-text="fullName" item-value="id" searchMethod="searchClients" itemKey="clients" variant="outlined" density="compact" hide-details="auto" />
            </div>
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Kontakt</span>
              <autocomplete v-model="form.contact" item-text="fullName" item-value="id" searchMethod="searchClients" itemKey="clients" variant="outlined" density="compact" hide-details="auto" :placeholder="managerOn ? 'Kontaktisik' : 'Haldur või muu kontaktisik'">
                <template v-if="!(form.contact && form.contact.id)" #append-inner><contact-add :typed="form.contact" :country-id="form.countryId" :city-id="form.cityId" @created="form.contact = $event" /></template>
              </autocomplete>
            </div>
            <div v-if="managerOn && isContract" class="obj-form__fld">
              <span class="obj-form__lbl">Haldusfirma</span>
              <autocomplete v-model="form.manager" item-text="fullName" item-value="id" searchMethod="searchClients" itemKey="clients" variant="outlined" density="compact" hide-details="auto" placeholder="Haldusfirma" />
              <span class="obj-form__hint">Logib kliendiportaali sama lepingu numbri ja oma telefoniga ning näeb kõiki maju, mida haldab.</span>
            </div>
            <div v-if="managerOn && isContract && form.manager && form.manager.id" class="obj-form__fld">
              <span class="obj-form__lbl">Aktid saadetakse</span>
              <v-radio-group v-model="form.aktRecipients" inline density="compact" hide-details>
                <v-radio v-for="option in aktRecipientOptions" :key="option.value" :label="option.label" :value="option.value" />
              </v-radio-group>
              <span class="obj-form__lbl mt-2">Koondarve maksja</span>
              <v-radio-group v-model="form.billTo" inline density="compact" hide-details>
                <v-radio v-for="option in billToOptions" :key="option.value" :label="option.label" :value="option.value" />
              </v-radio-group>
              <span class="obj-form__hint">Uus akt saab Maksjaks valitu; aktil saab seda muuta.</span>
            </div>
          </div>
        </div>

        <div v-if="isContract" class="obj-form__sec obj-form__sec--tint">
          <div class="obj-form__sh"><h2>Leping</h2><span>ainult lepingulisel objektil</span></div>
          <div class="obj-form__g3">
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Lepingu number</span>
              <v-text-field v-model="form.contract" variant="outlined" density="compact" hide-details="auto" />
            </div>
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Lepingu kuupäev</span>
              <datepicker v-model="form.contractStartDate" :outlined="true" :dense="true" />
            </div>
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Lepingu hind</span>
              <v-text-field v-model="form.contractPrice" variant="outlined" density="compact" hide-details="auto" suffix="€" />
            </div>
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Hoolduse intervall</span>
              <v-select v-model="form.contractInterval" :items="contractIntervalOptions" item-title="text" item-value="value" variant="outlined" density="compact" hide-details="auto" />
            </div>
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Reageerimisaeg</span>
              <v-select v-model="form.reactionTime" :items="reactionTimeOptions" item-title="text" item-value="value" variant="outlined" density="compact" hide-details="auto" />
            </div>
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Seotud tehnik</span>
              <v-select v-model="form.technic.id" :items="technics" item-title="fullName" item-value="id" variant="outlined" density="compact" hide-details="auto" clearable />
            </div>
          </div>
          <div class="obj-form__switches obj-form__switches--3">
            <button v-for="sw in contractSwitches" :key="sw.key" type="button" role="switch" class="obj-switch"
              :aria-checked="form[sw.key] ? 'true' : 'false'" @click="form[sw.key] = !form[sw.key]">
              <span class="obj-switch__track"><span class="obj-switch__knob" /></span>{{ sw.label }}
            </button>
          </div>
          <div v-if="form.extraService" class="obj-form__g3">
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Järgmine korraline TKK</span>
              <datepicker v-model="form.nextExtraServiceDate" :outlined="true" :dense="true" />
            </div>
          </div>
        </div>

        <div class="obj-form__sec">
          <div class="obj-form__sh"><h2>Kliendile</h2><span>meeldetuletused ja mida klient kliendikeskkonnas näeb</span></div>
          <div class="obj-form__switches">
            <button v-for="channel in reminderChannels" :key="channel.value" type="button" role="switch" class="obj-switch"
              :aria-checked="form.reminders.includes(channel.value) ? 'true' : 'false'" @click="toggleReminder(channel.value)">
              <span class="obj-switch__track"><span class="obj-switch__knob" /></span>{{ channel.label }}
            </button>
            <button v-for="sw in clientSwitches" :key="sw.key" type="button" role="switch" class="obj-switch"
              :aria-checked="form[sw.key] ? 'true' : 'false'" @click="form[sw.key] = !form[sw.key]">
              <span class="obj-switch__track"><span class="obj-switch__knob" /></span>{{ sw.label }}
            </button>
          </div>
        </div>

        <!-- Seadmed hang on the object, so a new one can only have them once it
             exists: there the button saves first and opens the Seadmed tab. -->
        <div class="obj-form__sec">
          <div class="obj-form__sh"><h2>Seadmed</h2><span>mis sellel aadressil paigaldatud on</span></div>
          <div class="obj-form__devices">
            <span v-if="objectId" class="obj-form__devtext">
              <b v-if="deviceText">{{ deviceText }}</b>
              <template v-else>Seadmeid pole lisatud.</template>
            </span>
            <span v-else class="obj-form__devtext">Seadmed lisatakse objektile pärast salvestamist.</span>
            <button v-if="objectId" type="button" class="obj-btn obj-btn--outline obj-btn--sm" @click="devicesDialog = true">Halda seadmeid</button>
            <button v-else type="button" class="obj-btn obj-btn--outline obj-btn--sm" :disabled="submitLoading" @click="saveThenDevices()">Salvesta ja halda seadmeid</button>
          </div>
        </div>

        <div class="obj-form__sec">
          <div class="obj-form__sh"><h2>Märkused</h2></div>
          <div class="obj-form__g1">
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Märkused (seadmed, ligipääs)</span>
              <v-textarea v-model="form.notes" variant="outlined" density="compact" hide-details="auto" rows="2" auto-grow />
            </div>
            <div class="obj-form__fld">
              <span class="obj-form__lbl">Kommentaarid</span>
              <v-textarea v-model="form.comments" variant="outlined" density="compact" hide-details="auto" rows="2" auto-grow />
            </div>
          </div>
          <button type="button" role="switch" class="obj-switch" :disabled="!(isAdmin || isSecretary)"
            :aria-checked="form.problematic ? 'true' : 'false'" @click="form.problematic = !form.problematic">
            <span class="obj-switch__track"><span class="obj-switch__knob" /></span>Probleemne objekt
            <span v-if="!(isAdmin || isSecretary)" class="obj-lsub">(muudavad admin ja sekretär)</span>
          </button>
          <!-- Hidden with the switch off, but the text stays: switched back on,
               the same reason returns. Read-only for whoever may not flip it. -->
          <template v-if="form.problematic">
            <div v-if="isAdmin || isSecretary" class="obj-form__fld obj-reason">
              <span class="obj-form__lbl">Põhjus</span>
              <v-textarea v-model="form.problematicNote" variant="outlined" density="compact" rows="2" auto-grow
                counter="1000" maxlength="1000" hint="Näevad kontor ja tehnikud. Klient portaalis ei näe." persistent-hint />
            </div>
            <div v-else-if="form.problematicNote" class="obj-reason obj-reason--read"><b>Põhjus:</b> {{ form.problematicNote }}</div>
          </template>
        </div>
      </v-form>

      <!-- What is about to be saved, as the object page will show it. -->
      <aside class="obj-card obj-card--plate obj-form__aside">
        <div class="obj-form__preview">
          <span class="obj-cap">{{ isContract ? 'LEPINGULINE OBJEKT' : 'TAVALINE OBJEKT' }}</span>
          <b class="obj-wide">{{ form.address || 'Aadress valimata' }}</b>
          <span>{{ previewPlace }}</span>
        </div>
        <div class="obj-form__pk"><span class="obj-cap">KLIENT</span><b>{{ form.client && form.client.fullName || '—' }}</b></div>
        <template v-if="isContract">
          <div class="obj-form__pk"><span class="obj-cap">LEPING</span><b>{{ contractLine }}</b></div>
          <div class="obj-form__pk"><span class="obj-cap">TEHNIK</span><b>{{ technicName || '—' }}</b></div>
          <div v-if="firstDeadline" class="obj-form__pk">
            <span class="obj-cap">{{ formType === types.CREATE ? 'ESIMENE TÄHTAEG' : 'UUS GRAAFIK ALATES' }}</span>
            <b>{{ firstDeadline }}</b>
            <span class="obj-lsub">tähtajad luuakse intervalli järgi</span>
          </div>
        </template>
        <div class="obj-form__actions">
          <button type="button" class="obj-btn obj-form__save" :disabled="submitLoading" @click="afterSave = null; save()">
            {{ submitLoading ? 'Salvestan…' : existing && formType === types.CREATE ? 'Salvesta siiski' : 'Salvesta' }}
          </button>
          <router-link :to="cancelLink" class="obj-btn obj-btn--outline obj-btn--muted obj-form__save">Katkesta</router-link>
        </div>
      </aside>
    </div>
    <v-dialog v-model="devicesDialog" max-width="900px" scrollable>
      <device-list
        v-if="devicesDialog && objectId"
        :objectId="objectId"
        :editable="!object || canEdit('object', object) || isTechnic"
        :deletable="!object || canDelete('device', object)"
        show-archived
        @changed="devices = $event"
      />
    </v-dialog>
    <v-dialog v-model="duplicateDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="text-h5">Objekt on juba olemas</v-card-title>
        <v-card-text>
          <p class="mb-3">Sellel aadressil on juba objekt olemas:</p>
          <p v-if="duplicateExisting" class="mb-1">
            <strong>{{ duplicateExisting.address }}</strong>
            <span v-if="duplicateExisting.client" class="ml-2 text-grey">— {{ duplicateExisting.client.fullName }}</span>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="duplicateDialog = false" variant="text">Tühista</v-btn>
          <v-btn color="success" :loading="submitLoading" @click="confirmCreateDuplicate">Salvesta siiski</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
  <loading-content v-else :loading="loading">
    <heading>
      <template v-if="formType === types.CREATE">Lisa uus objekt</template>
      <template v-else-if="object">{{$address(object.fullAddress)}}</template>
      <!-- Only for an object that exists: Vue Router 4 refuses to build a link
           without its objectId, where Vue Router 3 drew one to nowhere. -->
      <v-btn v-if="objectId" color="primary" :to="{ name: 'object/view/info', params: { objectId: objectId }}">Vaata</v-btn>
    </heading>
    <v-row>
      <v-col col="12" md="6" xl="4">
        <card>
          <v-form ref="observer" @submit.prevent>
            <v-select
              :rules="[rules.required]"
              v-model="form.type"
              :items="objectTypeOptions"
              label="Tüüp*"
              required
            />
            <Autocomplete
              :rules="[rules.required]"
              v-model="addressSearch"
              label="Aadress*"
              searchMethod="searchAdsAddresses"
              itemKey="objects"
              item-text="fullAddress"
              hide-no-data
            />
            <v-text-field
              v-model="form.postcode"
              label="Postiindeks"
              required
            />
            <v-select
              :rules="[rules.required]"
              v-model="form.countryId"
              :items="countries"
              item-title="title"
              item-value="id"
              label="Riik*"
              required
            />
            <v-select
              :rules="[rules.required]"
              v-model="form.cityId"
              :items="cities"
              item-title="title"
              item-value="id"
              label="Linn*"
              required
            />
            <autocomplete
              v-model="form.client"
              label="Klient"
              item-text="fullName"
              item-value="id"
              searchMethod="searchClients"
              itemKey="clients"
            />
            <autocomplete
              v-model="form.contact"
              label="Kontakt"
              item-text="fullName"
              item-value="id"
              searchMethod="searchClients"
              itemKey="clients"
            >
              <template v-if="!(form.contact && form.contact.id)" #append-inner><contact-add :typed="form.contact" :country-id="form.countryId" :city-id="form.cityId" @created="form.contact = $event" /></template>
            </autocomplete>
            <autocomplete
              v-if="managerOn && isContract"
              v-model="form.manager"
              label="Haldusfirma"
              item-text="fullName"
              item-value="id"
              searchMethod="searchClients"
              itemKey="clients"
              hint="Logib kliendiportaali sama lepingu numbri ja oma telefoniga ning näeb kõiki maju, mida haldab."
              persistent-hint
            />
            <template v-if="managerOn && isContract && form.manager && form.manager.id">
              <div class="text-caption text-grey-darken-1 mt-3">Aktid saadetakse</div>
              <v-radio-group v-model="form.aktRecipients" inline density="compact" hide-details>
                <v-radio v-for="option in aktRecipientOptions" :key="option.value" :label="option.label" :value="option.value" />
              </v-radio-group>
              <div class="text-caption text-grey-darken-1 mt-2">Koondarve maksja</div>
              <v-radio-group v-model="form.billTo" inline density="compact" hide-details>
                <v-radio v-for="option in billToOptions" :key="option.value" :label="option.label" :value="option.value" />
              </v-radio-group>
            </template>
            <v-text-field
              v-if="isContract"
              v-model="form.contract"
              label="Lepingu number"
            />
            <datepicker
              v-if="isContract"
              v-model="form.contractStartDate"
              label="Lepingu kuupäev"
            />
            <v-text-field
              v-if="isContract"
              v-model="form.contractPrice"
              label="Lepingu hind"
            />
            <v-select
              v-if="isContract"
              v-model="form.reactionTime"
              :items="reactionTimeOptions"
              label="Reageerimisaeg"
              required
            />
            <v-select
              v-if="isContract"
              v-model="form.contractInterval"
              :items="contractIntervalOptions"
              label="Hoolduse intervall"
              required
            />
            <v-checkbox
              v-if="isContract"
              v-model="form.extraService"
              label="Korraline TKK"
              type="checkbox"
            />
            <datepicker
              v-if="isContract"
              v-model="form.nextExtraServiceDate"
              label="Järgmine korraline TKK"
            />
            <v-checkbox
              v-if="isContract"
              v-model="form.active"
              label="Aktiivne"
              type="checkbox"
            />
            <v-checkbox
              v-if="isContract"
              v-model="form.key"
              label="Võti olemas"
              type="checkbox"
            />
            <v-select
              v-if="isContract"
              label="Seotud tehnik"
              v-model="form.technic.id"
              :items="technics"
              item-title="fullName"
              item-value="id"
            />
            <v-select 
              v-model="form.reminders"
              :items="['SMS', 'EMAIL']"
              label="Meeldetuletus"
              multiple
            />
            <v-checkbox
              class="mt-0"
              v-model="form.showNotesToClient"
              label="Näita märkusi kliendile"
              hide-details="auto"
            />
            <v-checkbox
              v-model="form.showPhotosToClient"
              label="Näita fotosid kliendile"
            />
            <v-textarea
              v-model="form.notes"
              label="Märkused"
            />
            <v-textarea
              v-model="form.comments"
              label="Kommentaarid"
            />
            <v-checkbox
              v-model="form.problematic"
              label="Problemaatiline"
              type="checkbox"
              :disabled="!(isAdmin || isSecretary)"
            />
            <v-textarea
              v-if="form.problematic"
              v-model="form.problematicNote"
              label="Põhjus"
              hint="Näevad kontor ja tehnikud. Klient portaalis ei näe."
              persistent-hint
              rows="2"
              auto-grow
              counter="1000"
              maxlength="1000"
              :readonly="!(isAdmin || isSecretary)"
            />
            <v-btn :loading="submitLoading" color="success" @click="_submit()">Salvesta</v-btn>
          </v-form>
        </card>
      </v-col>
    </v-row>

    <!-- Soft warning: an object already exists at this address -->
    <v-dialog v-model="duplicateDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="text-h5">Objekt on juba olemas</v-card-title>
        <v-card-text>
          <p class="mb-3">Sellel aadressil on juba objekt olemas:</p>
          <p v-if="duplicateExisting" class="mb-1">
            <strong>{{ duplicateExisting.address }}</strong>
            <span v-if="duplicateExisting.client" class="ml-2 text-grey">
              — {{ duplicateExisting.client.fullName }}
            </span>
          </p>
          <router-link
            v-if="duplicateExisting"
            :to="{ name: 'object/view/info', params: { objectId: duplicateExisting.id } }"
            target="_blank"
          >Vaata olemasolevat objekti</router-link>
          <v-alert type="warning" class="mt-4 mb-0" variant="tonal" density="compact">
            Aadressid ei ole alati unikaalsed (korterid, hoovimaja jne). Kas soovid sellele aadressile siiski uue objekti luua?
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="duplicateDialog = false" variant="text">Tühista</v-btn>
          <v-btn color="success" :loading="submitLoading" @click="confirmCreateDuplicate">Loo siiski</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </loading-content>
</template>

<script>
import api from '@/api'
import { matchCityId } from '@/plugins/adsCity'
import formMixin from '@/plugins/formMixin'
import rules from '@/plugins/rules'
import helpersMixin from '@/plugins/helpersMixin'
import { mapGetters, mapState } from 'vuex'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'
import loadArchivo from '@/plugins/archivo'
import '@/styles/object-plate.css'
import DeviceList from '@/components/DeviceList.vue'
import { deviceSummary } from '@/plugins/devices'

const MONTHS = ['Jaanuar', 'Veebruar', 'Märts', 'Aprill', 'Mai', 'Juuni', 'Juuli',
  'August', 'September', 'Oktoober', 'November', 'Detsember']

const types = {
  CREATE: 'create',
  UPDATE: 'update'
}

const empty = {
  type: 0,
  address: '',
  postcode: '',
  notes: '',
  comments: '',
  countryId: 1,
  cityId: 1,
  client: undefined,
  contact: undefined,
  manager: null,
  aktRecipients: 0,
  billTo: 0,
  technic: { id: undefined },
  active: false,
  problematic: false,
  problematicNote: '',
  key: false,
  extraService: false,
  nextExtraServiceDate: '',
  contract: '',
  contractStartDate: '',
  contractPrice: undefined,
  contractInterval: 0,
  reactionTime: 0,
  reminders: [],
  showNotesToClient: true,
  showPhotosToClient: true
}

export default {
  mixins: [ formMixin, helpersMixin ],
  components: { DeviceList },
  data () {
    return {
      rules,
      loading: false,
      object: undefined,
      types,
      countries: [],
      cities: [],
      allTechnics: [],
      addressSearch: '',
      duplicateDialog: false,
      duplicateExisting: null,
      // The new look: an object already at the picked address, and what the
      // ADS pick filled in (so the "aadressist" mark goes once it is changed).
      existing: null,
      fromAds: {},
      // Seadmed: the dialog on an edit, what is installed now (refreshed from
      // the dialog), and where a create goes once saved.
      devicesDialog: false,
      devices: null,
      afterSave: null,
      kinds: [
        { value: 1, label: 'Lepinguline', hint: 'Hooldusleping: tähtajad, tehnik, hind, katlamaja töös või väljas.' },
        { value: 0, label: 'Tavaline', hint: 'Ühekordsed tööd ja väljakutsed. Lepingut ega hooldusgraafikut pole.' }
      ].reverse(),
      contractSwitches: [
        { key: 'active', label: 'Katlamaja töös' },
        { key: 'key', label: 'Võti kontoris' },
        { key: 'extraService', label: 'Korraline TKK (järelvaataja)' }
      ],
      // Haldur: who gets the signed act, and whose Koondarve it lands on
      // (ObjectModel.aktRecipients / billTo on the server).
      aktRecipientOptions: [
        { value: 0, label: 'Klient' },
        { value: 1, label: 'Haldusfirma' },
        { value: 2, label: 'Mõlemad' }
      ],
      billToOptions: [
        { value: 0, label: 'Klient' },
        { value: 1, label: 'Haldusfirma' }
      ],
      clientSwitches: [
        { key: 'showNotesToClient', label: 'Näita märkusi kliendile' },
        { key: 'showPhotosToClient', label: 'Näita fotosid kliendile' }
      ],
      reminderChannels: [
        { value: 'EMAIL', label: 'Meeldetuletus e-postiga' },
        { value: 'SMS', label: 'Meeldetuletus SMS-iga' }
      ],
      form: {
       ...empty
      }
    }
  },
  computed: {
    ...mapGetters(['isTechnic', 'isAdmin', 'isSecretary']),
    ...mapState(['features']),
    plate () {
      return !!(this.features && this.features.objectPlate)
    },
    // Haldur (Seaded): a contract object's managing firm, a second portal login.
    managerOn () {
      return !!(this.features && this.features.manager)
    },
    deviceText () {
      const devices = this.devices || (this.object && this.object.devices) || []
      return deviceSummary(devices.filter(device => !device.archivedAt))
    },
    previewPlace () {
      const city = (this.cities.find(c => c.id === this.form.cityId) || {}).title
      return [city, this.form.postcode].filter(Boolean).join(', ') || 'vali aadress registrist'
    },
    // A technician whose account is switched off is not offered. The one the
    // object already has stays, so the object still shows his name.
    technics () {
      return this.allTechnics.filter(user => user.active !== false || user.id === (this.form.technic && this.form.technic.id))
    },
    technicName () {
      const id = this.form.technic && this.form.technic.id
      return id ? (this.technics.find(t => t.id === id) || {}).fullName : ''
    },
    contractLine () {
      const parts = []
      if (this.form.contract) parts.push(this.form.contract)
      if (this.form.contractPrice !== undefined && this.form.contractPrice !== '' && this.form.contractPrice !== null) {
        parts.push((parseFloat(String(this.form.contractPrice || 0).replace(',', '.')) || 0).toFixed(2).replace('.', ',') + ' €')
      }
      parts.push(this.contractIntervalOptionById(this.form.contractInterval).text.toLowerCase())
      return parts.join(' · ')
    },
    // Where the schedule will start, as service/intervalService.js counts it:
    // the end of the quarter after next month's quarter start, or of next month.
    firstDeadline () {
      // An edit only reschedules when the interval itself changes (the
      // object's afterSave hook), so otherwise there is nothing new to say.
      if (this.formType !== this.types.CREATE && (!this.object || this.object.contractInterval === this.form.contractInterval)) {
        return ''
      }
      let end
      if (this.form.contractInterval === this.contractIntervals.QUARETRLY) {
        const start = dayjs().add(3, 'month')
        end = start.month(Math.floor(start.month() / 3) * 3 + 2).endOf('month')
      } else if (this.form.contractInterval === this.contractIntervals.MONTHLY) {
        end = dayjs().add(1, 'month').endOf('month')
      } else {
        return ''
      }
      return MONTHS[end.month()] + ' ' + end.year()
    },
    cancelLink () {
      return this.objectId
        ? { name: 'object/view/info', params: { objectId: this.objectId } }
        : { name: 'object/list', params: { type: 'all' } }
    },
    objectId () {
      return this.$route.params.objectId
    },
    formType () {
      return this.objectId ? types.UPDATE : types.CREATE
    },
    isContract () {
      return this.form.type === this.objectTypes.CONTRACT
    },
    successMessage() {
      switch (this.formType) {
        case types.CREATE:
          return 'Uus objekt edukalt loodud'
        default:
          return 'Objekt edukalt uuendatud'
      }
    }
  },
  watch: {
    async objectId () {
      this.refresh()
    },
    addressSearch (val) {
      if (val && typeof val === 'object') {
        // Store only the street part; the city is kept separately (cityId) and
        // re-appended in fullAddress, so baking it in here duplicates it.
        this.form.address = val.streetAddress || ''
        this.form.postcode = val.postcode || ''
        const cityId = this.matchCityId(val)
        if (cityId) {
          this.form.cityId = cityId
        }
        this.fromAds = { postcode: this.form.postcode, cityId: cityId || null }
      } else {
        this.form.address = val || ''
        this.fromAds = {}
      }
    },
    'form.address' () {
      if (this.plate) this.checkAddress()
    },
    plate: {
      handler (on) {
        if (on) loadArchivo()
      },
      immediate: true
    },
    async 'form.countryId' (countryId) {
      const cityId = this.form.cityId
      this.form.cityId = undefined
      this.cities = []
      this.cities = await api.getCities(countryId)
      if (this.cities.find(({ id }) => id === cityId)) {
        this.form.cityId = cityId
      }
    }
  },
  methods: {
    // "Salvesta ja halda seadmeid" on a new object: the same save, landing on
    // the new object's Seadmed tab instead of Info.
    // Kept until the save lands, so the "Salvesta siiski" dialog of a matching
    // address still ends on the Seadmed tab; Salvesta itself clears it.
    saveThenDevices () {
      this.afterSave = 'devices'
      return this.save()
    },
    toggleReminder (value) {
      const reminders = this.form.reminders || []
      this.form.reminders = reminders.includes(value) ? reminders.filter(v => v !== value) : [...reminders, value]
    },
    // The new look's Salvesta: with the warning already on screen, a click is
    // the answer to it, so the save goes with force rather than asking again.
    async save () {
      if (this.existing && this.formType === this.types.CREATE) {
        const { valid } = await this.$refs.observer.validate()
        if (!valid) return
        return this.confirmCreateDuplicate()
      }
      return this._submit()
    },
    // An ADS pick onto the city list (plugins/adsCity.js).
    matchCityId (row) {
      return matchCityId(this.cities, row)
    },
    async refresh () {
      this.form = { ...empty }
      this.addressSearch = ''
      this.object = undefined
      this.existing = null
      this.devices = null
      if (!this.objectId) {
        // Clearing the address above validates it; a new form should not open
        // already red.
        this.$nextTick(() => this._reset())
        // "Salvesta ja lisa objekt" on a new client opens this with the client
        // already chosen.
        const clientId = parseInt(this.$route.query.clientId, 10)
        if (clientId) {
          api.getClient(clientId).then(({ client }) => {
            if (!this.objectId && client) this.form.client = { id: client.id, fullName: client.fullName }
          }).catch(() => {})
        }
        return
      }
      this.loading = true
      const { object } = await api.getObject(this.objectId)
      this.object = object
      Object.assign(this.form, object)
      // The server formats a missing date as "Invalid date"; the field should
      // read empty, not that, in either look.
      for (const field of ['contractStartDate', 'nextExtraServiceDate']) {
        if (this.form[field] === 'Invalid date') this.form[field] = ''
      }
      this.addressSearch = object.address
      this.form.reminders = []
      if (object.reminderEmail) {
        this.form.reminders.push('EMAIL')
      }
      if (object.reminderSms) {
        this.form.reminders.push('SMS')
      }
      this.loading = false
    },
    async handleForm (name, force = false) {
      const isContract = this.form.type === this.objectTypes.CONTRACT
      const request = {
        ...this.form,
        clientId: this.form.client && this.form.client.id || undefined,
        // null empties it: left out, the server keeps the stored Kontakt.
        contactId: this.form.contact && this.form.contact.id || null,
        // null empties it; left out while the switch is off, so it stays as is.
        managerClientId: this.managerOn ? (this.form.manager && this.form.manager.id || null) : undefined,
        aktRecipients: this.managerOn ? this.form.aktRecipients : undefined,
        billTo: this.managerOn ? this.form.billTo : undefined,
        technicId: isContract ? (this.form.technic && this.form.technic.id || undefined) : undefined,
        extraService: isContract ? this.form.extraService : false,
        reminderEmail: this.form.reminders.includes('EMAIL'),
        reminderSms: this.form.reminders.includes('SMS'),
        force,
      }
      try {
        if (this.formType === types.CREATE) {
          const { object } = await api.createObject(request)
          this.$router.push({ name: this.afterSave === 'devices' ? 'object/view/devices' : 'object/view/info', params: { objectId: object.id }})
        } else {
          await api.updateObject(this.objectId, request)
          this.$router.push({ name: 'object/view/info', params: { objectId: this.objectId }})
        }
        return true
      } catch (err) {
        // Same-address match is a soft warning: surface a confirm dialog instead
        // of an error, then let the user resend with force=true.
        if (err && err.duplicate) {
          this.duplicateExisting = err.existing
          this.duplicateDialog = true
          return false
        }
        throw err
      }
    },
    async confirmCreateDuplicate () {
      this.duplicateDialog = false
      this.submitLoading = true
      try {
        if (await this.handleForm('observer', true) && this.successMessage) {
          this.showNotification({ text: this.successMessage, color: 'success' })
        }
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.submitLoading = false
      }
    }
  },
  created () {
    this.checkAddress = debounce(async () => {
      const address = (this.form.address || '').trim()
      if (address.length < 2) {
        this.existing = null
        return
      }
      try {
        const { existing } = await api.checkObjectAddress({ address, excludeId: this.objectId || undefined })
        if (address === (this.form.address || '').trim()) this.existing = existing
      } catch (err) {
        this.existing = null
      }
    }, 400)
  },
  mounted () {
    this.refresh()
    api.getCountries().then(countries => {
      this.countries = countries
    })
    api.getCities(1).then(cities => {
      this.cities = cities
    })
    api.getTechnics().then(({ users }) => {
      this.allTechnics = users
    })
  }
}
</script>